import React from "react";
import { Tooltip } from "@mui/material";
import { useNavigate } from "react-router";
import { useTranslation } from "react-i18next";
import { Order } from "../../../../core/models/entities/Order";
import {
  useCanAdjustOrderPrices,
  useCanPayOrders,
} from "../../../components/Order/PayNow";
import {
  orderIsPriceAdjusted,
  priceAdjustmentsDetailsUrl,
} from "../../order-details/priceAdjustmentDisplay";

interface Props {
  order: Order;
}

const badgeClass =
  "mt-1 inline-flex w-fit items-center rounded-full border border-blue-300 " +
  "bg-blue-50 px-2 py-0.5 text-xs font-semibold text-blue-800";

// "Adjusted vN" under the status of an order-list row whose prices were
// adjusted. Self-gating (FE flag + a role that meets the payment link,
// Vendor/Admin + an adjusted row payload) and derived from the row payload
// alone - no call on list rendering. For the admin it is a button opening
// the order details with the "Price adjustments" panel expanded (the click
// never bubbles to the row's own navigation, /order/confirm); for the
// paying vendor a plain chip whose tooltip says the payment link was
// renewed. Today's list payload (GetOrders) carries neither the summary
// nor the agreed prices, so the chip waits for the API follow-up.
export function PriceAdjustedBadge({ order }: Props): React.ReactElement | null {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const canSee = useCanPayOrders();
  const isAdmin = useCanAdjustOrderPrices();
  if (!canSee || !orderIsPriceAdjusted(order)) return null;
  const version = order.priceAdjustment?.version;
  const label = version
    ? t("orderPriceAdjustment.adjustedBadgeVersion", { version })
    : t("orderPriceAdjustment.adjustedBadge");
  if (!isAdmin) {
    return (
      <Tooltip
        title={t("orderPriceAdjustment.adjustedBadgeTooltipCustomer")}
        placement={"top-start"}
      >
        <span className={badgeClass} data-testid="price-adjusted-badge">
          {label}
        </span>
      </Tooltip>
    );
  }
  return (
    <Tooltip title={t("orderPriceAdjustment.adjustedBadgeTooltip")} placement={"top-start"}>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          navigate(priceAdjustmentsDetailsUrl(order));
        }}
        className={`${badgeClass} hover:bg-blue-100`}
        data-testid="price-adjusted-badge"
      >
        {label}
      </button>
    </Tooltip>
  );
}
